// =============================================================================
// api/security.ts
// Client-side hooks into the security audit log (security_events table).
// All calls are fire-and-forget: logging must never block or break a user flow.
// =============================================================================

import { supabase } from "@cl/supabase";
import { normalizeEmail } from "@u/contactIdentity";

export type SecuritySeverity = "info" | "warning" | "critical";

/**
 * Append a security audit event via the `log_security_event` RPC.
 * SECURITY DEFINER on the DB side forces user_id = auth.uid() (null for anon),
 * so a failed sign-in still logs even with no session. Never throws.
 */
export async function logSecurityEvent(
  eventType: string,
  severity: SecuritySeverity = "info",
  detail?: Record<string, unknown>,
  route?: string,
): Promise<void> {
  try {
    await supabase.rpc("log_security_event", {
      p_event_type: eventType,
      p_severity: severity,
      p_detail: detail ?? {},
      p_route: route ?? (typeof window !== "undefined" ? window.location.pathname : null),
    });
  } catch {
    /* fire-and-forget — logging failure must not surface to the user */
  }
}

export interface SecurityEvent {
  id: string;
  event_type: string;
  severity: SecuritySeverity;
  ip: string | null;
  route: string | null;
  detail: Record<string, unknown>;
  created_at: string;
}

/**
 * Recent audit events for one user (admin-only; RLS `admin_read_security_events`
 * gates the read). Newest first. Returns [] on error — a read-only diagnostic
 * panel must never break the page.
 */
export async function fetchUserSecurityEvents(
  userId: string,
  limit = 20,
): Promise<SecurityEvent[]> {
  const { data, error } = await supabase
    .from("security_events")
    .select("id,event_type,severity,ip,route,detail,created_at")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) return [];
  return (data ?? []) as SecurityEvent[];
}

/**
 * Convenience wrapper for failed authentication attempts.
 * Records the attempted email (lowercased) and reason, never the password.
 */
export function logAuthFailure(
  action: "signin" | "signup" | "reauth" | "reset" | "oauth",
  email: string | undefined,
  reason: string | undefined,
): void {
  void logSecurityEvent(`auth.${action}.failed`, "warning", {
    email: normalizeEmail(email),
    reason: reason?.slice(0, 200) || null,
  });
}

